/**
 * The filters over array-valued cells -- lookups, and formulas returning arrays.
 *
 * All twenty ask the same question of a list: does any entry satisfy this, or
 * does none. What varies is how an entry is read, and that depends on the type
 * the array holds: a lookup of a number compares numerically, a lookup of a
 * single select compares option ids, and a lookup of a multiple select has to
 * reach one level deeper because each entry is itself a list of options.
 *
 * Upstream expresses that as mixins composed onto formula subtypes, four layers
 * deep in places. Here it is one table from subtype to predicates, which is the
 * same dispatch written down where it can be read.
 */
import {
  ComparisonOperator,
  hasAllValuesEqual,
  hasEmptyValue,
  hasValueContains,
  hasValueContainsWord,
  hasValueEqual,
  hasValueLengthLowerThan,
  isArray,
  numericHasValueComparableTo,
  type ArrayCellItem,
} from './predicates'
import { stringify } from './handlers'
import { parseNumberValue } from '../fields/numbers'
import type { Field } from '@/api/types'
import type { MatchResult } from './types'

type Cell = readonly ArrayCellItem[] | null | undefined
type Predicate = (cell: Cell, filterValue: unknown) => boolean

/** What one array element type can be asked. A null means "not supported". */
interface ArraySupport {
  hasEmptyValue: Predicate | null
  hasValueEqual: Predicate | null
  hasAllValuesEqual: Predicate | null
  hasValueContains: Predicate | null
  hasValueContainsWord: Predicate | null
  hasValueLengthLowerThan: Predicate | null
  /** Ordering comparisons, which only the numeric types answer. */
  comparable: ((operator: ComparisonOperator) => Predicate) | null
}

const NOTHING: ArraySupport = {
  hasEmptyValue: null,
  hasValueEqual: null,
  hasAllValuesEqual: null,
  hasValueContains: null,
  hasValueContainsWord: null,
  hasValueLengthLowerThan: null,
  comparable: null,
}

/* ------------------------------------------------------- entry readers */

/**
 * Flattens a lookup of single selects to the option ids, as text.
 *
 * The stored entry is `{ id, value: { id, value, color } }` -- the outer id
 * belongs to the linked row and the inner one to the option -- so an id filter
 * has to read one level in.
 */
const asOptionIds = (cell: Cell): ArrayCellItem[] =>
  (isArray(cell) ? cell : []).map((entry) => ({
    id: entry.id,
    value: stringify((entry.value as { id?: unknown } | null)?.id ?? ''),
  }))

/** Flattens a lookup of single selects to the option labels. */
const asOptionLabels = (cell: Cell): ArrayCellItem[] =>
  (isArray(cell) ? cell : []).map((entry) => ({
    id: entry.id,
    value: (entry.value as { value?: unknown } | null)?.value ?? '',
  }))

/**
 * Whether any entry's own list of options satisfies the predicate.
 *
 * A lookup of a multiple select nests: the cell is a list of linked rows, and
 * each row's value is that row's list of options.
 */
function nested(predicate: (inner: Cell, filterValue: unknown) => boolean): Predicate {
  return (cell, filterValue) => {
    if (!isArray(cell) || cell.length === 0) return false
    return cell.some((entry) =>
      predicate((entry.value ?? []) as ArrayCellItem[], filterValue),
    )
  }
}

/** The option ids across every entry of a nested select lookup. */
function nestedOptionIds(cell: Cell): Set<number> {
  if (!isArray(cell)) return new Set()
  return new Set(
    cell.flatMap((entry) =>
      ((entry.value ?? []) as { id?: unknown }[]).map((option) => option.id),
    ) as number[],
  )
}

function parseIds(filterValue: string): number[] {
  return stringify(filterValue)
    .trim()
    .split(',')
    .map((part) => parseInt(part, 10))
}

/* ------------------------------------------------------------ the table */

const textLike: ArraySupport = {
  hasEmptyValue,
  hasValueEqual: (cell, filterValue) => hasValueEqual(cell, filterValue),
  hasAllValuesEqual: (cell, filterValue) => hasAllValuesEqual(cell, filterValue),
  hasValueContains: (cell, filterValue) => hasValueContains(cell, filterValue),
  hasValueContainsWord: (cell, filterValue) => hasValueContainsWord(cell, filterValue),
  hasValueLengthLowerThan: (cell, filterValue) =>
    hasValueLengthLowerThan(cell, filterValue),
  comparable: null,
}

/**
 * Numbers, compared in arbitrary precision.
 *
 * `hasValueContains` stays a text match -- a lookup of numbers is still
 * substring-searchable, which is how somebody finds every row whose total
 * contains "99".
 */
const numeric: ArraySupport = {
  ...NOTHING,
  hasEmptyValue,
  hasValueContains: (cell, filterValue) => hasValueContains(cell, filterValue),
  hasAllValuesEqual: (cell, filterValue) => hasAllValuesEqual(cell, filterValue),
  hasValueEqual: numericHasValueComparableTo(ComparisonOperator.EQUAL),
  comparable: (operator) => numericHasValueComparableTo(operator),
}

/** A duration is a number of seconds, and is not substring-searchable. */
const duration: ArraySupport = {
  ...NOTHING,
  hasEmptyValue,
  hasValueEqual: numericHasValueComparableTo(ComparisonOperator.EQUAL),
  comparable: (operator) => numericHasValueComparableTo(operator),
}

/** A date renders as text, so it can be searched but not equated. */
const dateLike: ArraySupport = {
  ...NOTHING,
  hasEmptyValue,
  hasValueContains: (cell, filterValue) => hasValueContains(cell, filterValue),
}

const singleSelect: ArraySupport = {
  ...NOTHING,
  hasEmptyValue,
  // Any of the comma-separated ids matching is enough.
  hasValueEqual: (cell, filterValue) =>
    stringify(filterValue)
      .trim()
      .split(',')
      .some((one) => hasValueEqual(asOptionIds(cell), one)),
  hasValueContains: (cell, filterValue) =>
    hasValueContains(asOptionLabels(cell), filterValue),
  hasValueContainsWord: (cell, filterValue) =>
    hasValueContainsWord(asOptionLabels(cell), filterValue),
}

const multipleSelect: ArraySupport = {
  ...NOTHING,
  hasEmptyValue,
  hasValueContains: nested((inner, filterValue) => hasValueContains(inner, filterValue)),
  hasValueContainsWord: nested((inner, filterValue) =>
    hasValueContainsWord(inner, filterValue),
  ),
  // Set equality, not membership: the filter asks for a linked row holding
  // exactly these options and no others. `has_any_select_option_equal` below is
  // the membership question, which is why both exist.
  hasValueEqual: (cell, filterValue) => {
    if (!isArray(cell)) return false
    const wanted = new Set(parseIds(stringify(filterValue)))
    return cell.some((entry) => {
      // A linked row whose value is not a list of options has no set to compare.
      if (!Array.isArray(entry.value)) return false
      const ids = new Set((entry.value as { id?: unknown }[]).map((o) => o.id))
      return ids.size === wanted.size && [...ids].every((id) => wanted.has(id as number))
    })
  },
}

/**
 * Which predicates each array element type answers.
 *
 * Keyed by a formula's `array_formula_type`. An entry type not listed answers
 * nothing, so a filter over it matches no row rather than throwing.
 */
const ARRAY_SUPPORT: Record<string, ArraySupport> = {
  text: textLike,
  char: textLike,
  url: textLike,
  uuid: textLike,
  number: numeric,
  duration,
  date: dateLike,
  date_interval: dateLike,
  // A checkbox filter value has already been parsed to a real boolean by
  // `parseArrayFilterValue`, so these compare against `true`/`false` rather
  // than against the words.
  boolean: {
    ...NOTHING,
    hasEmptyValue,
    hasValueEqual: (cell, filterValue) => hasValueEqual(cell, filterValue),
    hasAllValuesEqual: (cell, filterValue) => hasAllValuesEqual(cell, filterValue),
    // The substring filters do not pre-parse, so this one reads the words
    // itself: "Red" against a checkbox column means `false`, and matches the
    // rows that are unticked rather than the rows whose text says Red.
    hasValueContains: (cell, filterValue) =>
      hasValueContains(cell, parseBoolean(stringify(filterValue))),
  },
  single_select: singleSelect,
  multiple_select: multipleSelect,
  multiple_collaborators: multipleSelect,
}

const TRUE_VALUES = ['y', 't', 'o', 'yes', 'true', 'on', '1']
function parseBoolean(value: string): boolean {
  return TRUE_VALUES.includes(stringify(value).toLowerCase().trim())
}

/**
 * Reads the filter value the way the array's element type would.
 *
 * This runs *before* the empty-value check, which is what makes a nonsense
 * filter on a numeric column show every row: the number parser answers `''` for
 * anything it cannot read, and `''` is the value that abstains. Only the number
 * and boolean types parse at all; every other element type takes the text as
 * typed.
 */
function parseArrayFilterValue(field: Field, filterValue: string): unknown {
  const entryType = elementTypeOf(field)
  if (entryType === 'number') {
    const parsed = parseNumberValue(field as never, filterValue, false)
    return parsed === null || parsed.isNaN() ? '' : parsed.toString()
  }
  if (entryType === 'boolean') return parseBoolean(filterValue)
  return filterValue
}

/**
 * The element type a field's array filters answer as.
 *
 * A formula whose result *is* an array reports the element type separately, in
 * `array_formula_type`; one whose result is a plain value reports it in
 * `formula_type` and has no element type. Reading `array_formula_type`
 * unconditionally answers for the wrong type on every non-array formula.
 */
function elementTypeOf(field: Field): string {
  const { formula_type: formulaType, array_formula_type: arrayType } = field as {
    formula_type?: string
    array_formula_type?: string
  }
  return (formulaType === 'array' ? arrayType : formulaType) ?? ''
}

/** The predicates available for a field's array element type. */
function supportFor(field: Field): ArraySupport {
  return ARRAY_SUPPORT[elementTypeOf(field)] ?? NOTHING
}

/* ---------------------------------------------------------- the filters */

/**
 * Builds an array filter.
 *
 * An empty filter value matches every row: the filter has not been filled in.
 * A field whose element type does not answer this predicate matches nothing,
 * except under negation, where it matches everything -- the same shape as
 * upstream's `filterValue === '' || !fn(...)`.
 *
 * `parses` says whether the element type reads the filter value before the
 * empty check. The equality and ordering filters do, which is what makes a
 * nonsense value on a numeric column abstain rather than match nothing; the
 * substring filters do not, and instead treat a value that is only whitespace
 * as empty.
 */
function arrayFilter(
  pick: (support: ArraySupport) => Predicate | null,
  { negate = false, parses = true }: { negate?: boolean; parses?: boolean } = {},
) {
  return (rowValue: unknown, filterValue: string, field: Field): MatchResult => {
    const value = parses ? parseArrayFilterValue(field, filterValue) : filterValue
    if (parses ? value === '' : filterValue.trim() === '') return true
    const predicate = pick(supportFor(field))
    if (predicate === null) return negate
    const matched = predicate(rowValue as Cell, value)
    return negate ? !matched : matched
  }
}

/** `has_empty_value` takes no filter value, so it never abstains. */
function emptyValueFilter(negate: boolean) {
  return (rowValue: unknown, _filterValue: string, field: Field): MatchResult => {
    const predicate = supportFor(field).hasEmptyValue
    if (predicate === null) return negate
    const matched = predicate(rowValue as Cell, '')
    return negate ? !matched : matched
  }
}

function comparisonFilter(operator: ComparisonOperator, negate = false) {
  return arrayFilter(
    (support) => (support.comparable === null ? null : support.comparable(operator)),
    { negate },
  )
}

export const matchesHasEmptyValue = emptyValueFilter(false)
export const matchesHasNotEmptyValue = emptyValueFilter(true)

export const matchesHasValueEqual = arrayFilter((s) => s.hasValueEqual)
export const matchesHasNotValueEqual = arrayFilter((s) => s.hasValueEqual, {
  negate: true,
})
export const matchesHasAllValuesEqual = arrayFilter((s) => s.hasAllValuesEqual)
export const matchesHasValueContains = arrayFilter((s) => s.hasValueContains, {
  parses: false,
})
export const matchesHasNotValueContains = arrayFilter((s) => s.hasValueContains, {
  negate: true,
  parses: false,
})
export const matchesHasValueContainsWord = arrayFilter((s) => s.hasValueContainsWord, {
  parses: false,
})
export const matchesHasNotValueContainsWord = arrayFilter((s) => s.hasValueContainsWord, {
  negate: true,
  parses: false,
})
/**
 * `has_value_length_is_lower_than` does not abstain on an empty filter value.
 *
 * Upstream calls the predicate directly with no `filterValue === ''` guard, so
 * a cleared filter compares against a length of zero and matches nothing --
 * unlike every other filter in this module.
 */
export function matchesHasValueLengthIsLowerThan(
  rowValue: unknown,
  filterValue: string,
  field: Field,
): MatchResult {
  const predicate = supportFor(field).hasValueLengthLowerThan
  if (predicate === null) return false
  return predicate(rowValue as Cell, filterValue)
}

export const matchesHasValueHigher = comparisonFilter(ComparisonOperator.HIGHER_THAN)
export const matchesHasNotValueHigher = comparisonFilter(
  ComparisonOperator.HIGHER_THAN,
  true,
)
export const matchesHasValueHigherOrEqual = comparisonFilter(
  ComparisonOperator.HIGHER_THAN_OR_EQUAL,
)
export const matchesHasNotValueHigherOrEqual = comparisonFilter(
  ComparisonOperator.HIGHER_THAN_OR_EQUAL,
  true,
)
export const matchesHasValueLower = comparisonFilter(ComparisonOperator.LOWER_THAN)
export const matchesHasNotValueLower = comparisonFilter(
  ComparisonOperator.LOWER_THAN,
  true,
)
export const matchesHasValueLowerOrEqual = comparisonFilter(
  ComparisonOperator.LOWER_THAN_OR_EQUAL,
)
export const matchesHasNotValueLowerOrEqual = comparisonFilter(
  ComparisonOperator.LOWER_THAN_OR_EQUAL,
  true,
)

/**
 * Whether any linked row carries any of the named options.
 *
 * Distinct from `has_value_equal` on a single select: that compares each entry's
 * one option, while this pools the options across every linked row first,
 * because each row holds a list.
 */
export function matchesHasAnySelectOptionEqual(
  rowValue: unknown,
  filterValue: string,
): MatchResult {
  if (filterValue === '') return true
  const present = nestedOptionIds(rowValue as Cell)
  return parseIds(filterValue).some((id) => present.has(id))
}

export function matchesHasNoneSelectOptionEqual(
  rowValue: unknown,
  filterValue: string,
): MatchResult {
  if (filterValue === '') return true
  const present = nestedOptionIds(rowValue as Cell)
  return !parseIds(filterValue).some((id) => present.has(id))
}
